import React from 'react';
import { StackShim as Stack, StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import { Label, makeStyles, tokens } from '@fluentui/react-components';
import {
  CommentRegular,
  ClipboardTaskAddRegular,
  CheckmarkCircleRegular,
  DocumentArrowUpRegular,
  DocumentDismissRegular,
  EditPersonRegular,
  FlagRegular,
} from '@fluentui/react-icons';
import moment from 'moment';
import ActivityItem from '../../common/ActivityItem';
import { IEntityTerminationTask, LegalInProcessTaskActionType } from '../types';

import StatDateFormats from '../../../utils/types/DateFormats';
import { StatUser } from '../../../utils/statApi/UsersApi';

interface IActionItemAction {
  id: number;
  actionDate: Date;
  actionValue: string;
  changedBy?: StatUser;
  actionType: string;
  comments: string;
}

const useStyles = makeStyles({
  activityItem: {
    marginTop: '10px',
  },
  historyStack: {
    marginBottom: '20px',
  },
  stickyHeader: {
    position: 'sticky',
    top: '0',
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

const LegalInProcessTaskHistory = ({
  selectedtask,
}: {
  selectedtask: IEntityTerminationTask;
}): JSX.Element => {
  const styles = useStyles();

  const actionUpdates: IActionItemAction[] = selectedtask?.taskHistories
    ? selectedtask.taskHistories.map((update) => ({
        id: update.id,
        actionDate: update.actionDate,
        actionValue: update.updatedValue,
        changedBy: update.changedBy,
        actionType: update.actionType,
        comments: '',
      }))
    : [];

  // Sorted descending by actionDate
  const sortedActionItemActions = (actions: IActionItemAction[]): IActionItemAction[] => {
    return actions.sort((a, b) => {
      return new Date(b.actionDate).getTime() - new Date(a.actionDate).getTime();
    });
  };

  const sortedActions = sortedActionItemActions(actionUpdates);

  const completedDateText = (value: string, user: string, item: IActionItemAction): string => {
    if (value) {
      return `${user} changed the Completed Date to: "${value}".`;
    }
    return `${user} cleared the Completed Date.`;
  };

  const followUpDateText = (value: string, user: string): string => {
    if (value) {
      return `${user} changed the Follow-Up Flag Date to: "${value}".`;
    }
    return `${user} cleared the Follow-Up Flag Date.`;
  };

  const renderActivityItem = (item: IActionItemAction): JSX.Element => {
    const user = item.changedBy?.displayName;
    let description = '';
    let icon = null;
    let comments = '';

    switch (item.actionType) {
      case LegalInProcessTaskActionType.CreatedTask:
        description = `${user} created the task.`;
        comments = item.comments;
        icon = <ClipboardTaskAddRegular />;
        break;
      case LegalInProcessTaskActionType.CompletedTask:
        description = completedDateText(item.actionValue, user, item);
        comments = item.comments;
        icon = <CheckmarkCircleRegular />;
        break;
      case LegalInProcessTaskActionType.UpdatedOwner:
        description = `${user} updated the owner to: ${item.actionValue}.`;
        comments = item.comments;
        icon = <EditPersonRegular />;
        break;
      case LegalInProcessTaskActionType.UpdatedAssignee:
        description = `${user} updated the assignee(s) to: ${item.actionValue}.`;
        comments = item.comments;
        icon = <EditPersonRegular />;
        break;
      case LegalInProcessTaskActionType.PostedResponse:
        description = `${user} updated response to: "${item.actionValue}".`;
        comments = item.comments;
        icon = <CommentRegular />;
        break;
      case LegalInProcessTaskActionType.UpdatedComment:
        description = `${user} updated the additional task to: "${item.actionValue}".`;
        comments = item.comments;
        icon = <CommentRegular />;
        break;
      case LegalInProcessTaskActionType.UpdatedFollowUpFlagDate:
        description = followUpDateText(item.actionValue, user);
        comments = item.comments;
        icon = <FlagRegular />;
        break;
      case LegalInProcessTaskActionType.UploadedDocument:
        description = `${user} uploaded document(s): ${item.actionValue}.`;
        comments = item.comments;
        icon = <DocumentArrowUpRegular />;
        break;
      case LegalInProcessTaskActionType.RemovedDocument:
        description = `${user} removed document(s): ${item.actionValue}.`;
        comments = item.comments;
        icon = <DocumentDismissRegular />;
        break;
      default:
        break;
    }
    return (
      <ActivityItem
        key={item.id}
        timeStamp={moment(item.actionDate).format(StatDateFormats.DayWithTime)}
        activityDescription={description}
        className={styles.activityItem}
        activityIcon={icon}
        comments={comments}
      />
    );
  };
  return (
    <Stack tokens={{ childrenGap: 10 }} className={styles.historyStack}>
      <div className={styles.stickyHeader}>
        <Label>History</Label>
      </div>
      <StackItem>
        {sortedActions?.length === 0 && <span>No history available.</span>}
        {sortedActions?.map((item: IActionItemAction) => {
          return renderActivityItem(item);
        })}
      </StackItem>
    </Stack>
  );
};

export default LegalInProcessTaskHistory;
